import React, { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "utilities/i18n";
import MaskedInput from "react-text-mask";
import {
  AvailableLicenseFragment,
  useVerifyLicenseKeyQuery
} from "generated/graphql";
import { Link, useLocation } from "polyrouter";
import { LoadingIndicator, PolyIcon } from "elements";
import { sortTableData } from "collections/Table";
import licensingCard from "assets/images/licensing-card.svg";
import polyPlusLicense from "assets/images/poly-plus-license.svg";
import {
  ActionType,
  ActivationStage,
  useActivationReducer
} from "./useActivationReducer";
import "./LicenseActivation.scss";

const ProgressBar: React.FC<{
  licenseKeys: string[];
  models: string[];
  stage: ActivationStage;
  onDelete: (payload: { licenseKey: string }) => void;
}> = ({ licenseKeys, models, stage, onDelete }) => {
  const { t } = useTranslation();

  let licenseKeyClass, reviewEntitlementClass;
  switch (stage) {
    case ActivationStage.InputLicenseKeys:
      licenseKeyClass = "active";
      reviewEntitlementClass = "inactive";
      break;
    case ActivationStage.ReviewLicenseInfo:
    case ActivationStage.ActivationInitiated:
    case ActivationStage.ActivationInProgress:
      licenseKeyClass = "completed";
      reviewEntitlementClass = "active";
      break;
    case ActivationStage.Completed:
      licenseKeyClass = "completed";
      reviewEntitlementClass = "completed";
      break;
  }

  return (
    <div className="setup-progress">
      <div className={licenseKeyClass}>
        <div>
          <div className="circle">
            <div className="check" />
          </div>
          <div className="bar" />
        </div>
        <div>
          <label>{t("ACTIVATION_KEYS")}</label>
          {licenseKeys.map((licenseKey, i) => (
            <div className="entered-key d-flex" key={`entered-key-${i}`}>
              <p className="mr-5">{licenseKey}</p>
              {stage < ActivationStage.ActivationInitiated && (
                <PolyIcon
                  icon="clear_x"
                  onClick={() => onDelete({ licenseKey })}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className={reviewEntitlementClass}>
        <div>
          <div className="circle">
            <div className="check" />
          </div>
        </div>
        <div>
          <label>{t("REVIEW_ENTITLEMENTS")}</label>
          {stage === ActivationStage.Completed &&
            models.map((model, i) => (
              <div className="entitled-model" key={`entitled-model-${i}`}>
                <p>{model}</p>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

const LicenseKeyForm: React.FC<{
  previousKeys: string[];
  onAdd: (payload: {
    licenseKey: string;
    licenses: AvailableLicenseFragment[];
  }) => void;
  onSubmit: (payload?: {
    licenseKey: string;
    licenses: AvailableLicenseFragment[];
  }) => void;
}> = ({ previousKeys, onAdd, onSubmit }) => {
  const { t } = useTranslation();
  const [pendingAction, setPendingAction] = useState<"ADD" | "SUBMIT" | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [licenseKey, setLicenseKey] = useState("");
  const isValidInput = (licenseKey.match(/\d/g)?.length ?? 0) === 16;
  const { data, fetching, stale } = useVerifyLicenseKeyQuery({
    variables: { licenseKey },
    pause: !pendingAction || !isValidInput
  });

  const handleKeyEntry = useCallback(() => {
    /* Allow empty submit if keys previously entered */
    if (
      pendingAction === "SUBMIT" &&
      licenseKey.length === 0 &&
      previousKeys.length !== 0
    ) {
      onSubmit();
      return;
    }

    if (!isValidInput) {
      setErrorMessage(t("LICENSE_KEY_INVALID"));
      return;
    }

    if (!data) {
      return;
    }
    const { success, error, availableLicenses } = data.verifyLicenseKey;

    if (!success && error) {
      setErrorMessage(t(error));
    }

    if (success && availableLicenses) {
      if (pendingAction === "ADD") {
        onAdd({ licenseKey, licenses: availableLicenses });
      }
      if (pendingAction === "SUBMIT") {
        onSubmit({ licenseKey, licenses: availableLicenses });
      }
      setLicenseKey("");
    }
  }, [
    t,
    data,
    pendingAction,
    licenseKey,
    isValidInput,
    previousKeys,
    onAdd,
    onSubmit
  ]);

  useEffect(() => {
    if (pendingAction && !fetching && !stale) {
      handleKeyEntry();
      setPendingAction(null);
    }
  }, [pendingAction, fetching, stale, handleKeyEntry]);

  return (
    <form
      data-testid="license-key-form"
      className="d-flex flex-column"
      onKeyDown={e => {
        if (e.code === "Space") setPendingAction("ADD");
        setErrorMessage(null);
      }}
      onSubmit={e => {
        e.preventDefault();
        setPendingAction("SUBMIT");
      }}
    >
      <img src={licensingCard} alt="License Key Form" />
      <div>
        <label htmlFor="key-input">{t("ACTIVATION_KEY")}</label>
        <MaskedInput
          id="key-input"
          value={licenseKey}
          onChange={e => setLicenseKey(e.target.value)}
          placeholder="L0000-0000-0000-0000"
          placeholderChar="_"
          size={28}
          autoComplete="off"
          mask={() =>
            Array(20)
              .fill(/[A-Za-z0-9]/)
              .map((x, i) => (i && i % 5 === 0 ? "-" : x))
          }
        />
      </div>
      {fetching ? (
        <p className="validation-message">{t("LICENSE_KEY_VALIDATING")}</p>
      ) : (
        <p className="error-message">{errorMessage}</p>
      )}
      <button className="btn btn-primary mt-2">{t("NEXT")}</button>
    </form>
  );
};

const LicenseInfoTable: React.FC<{ tableData: Record<string, string>[] }> = ({
  tableData
}) => {
  const { t } = useTranslation();
  const columns = [
    { key: "sku", label: t("DEVICE_MODEL") },
    { key: "licenseKey", label: t("LICENSE_KEY") },
    { key: "quantity", label: t("QUANTITY") },
    { key: "assigned", label: t("EXPIRATION") }
  ];

  sortTableData(tableData, { field: "model", direction: "ASC" });

  return (
    <div className="license-info-table" data-testid="license-info-table">
      <table className="table">
        <thead>
          <tr>
            {columns.map(({ key, label }) => (
              <th key={key}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableData.map(({ model, licenseKey, quantity, endDate }, i) => (
            <tr key={`license-info-${i}`}>
              <td>{model}</td>
              <td>{licenseKey}</td>
              <td>{quantity}</td>
              <td>{endDate}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const LicenseActivation: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [
    { stage, licensesByKey, tableData },
    dispatch
  ] = useActivationReducer();
  const licenseKeys = Object.keys(licensesByKey);
  const models = Array.from(new Set(tableData.map(({ model }) => model)));

  return (
    <div className="poly-plus-activate card d-flex flex-column">
      <div className="setup-section-header d-flex align-center mb-3 pl-5">
        <span className="title-capitalized">
          {t("LICENSE_ACTIVATION_TITLE")}
        </span>
        <Link to={pathname.split("/activate")[0]}>
          <PolyIcon className="m-0" icon="clear_circle_outline" />
        </Link>
      </div>

      <div className="activate-content d-flex mb-3">
        <ProgressBar
          licenseKeys={licenseKeys}
          models={models}
          stage={stage}
          onDelete={payload =>
            dispatch({
              type: ActionType.DeleteLicense,
              payload
            })
          }
        />
        <div className="activate-divider mh-5" />
        <div className="activate-main d-flex flex-column">
          {(() => {
            switch (stage) {
              case ActivationStage.InputLicenseKeys:
                return (
                  <>
                    <h4 className="mb-5">
                      {t("LICENSE_ACTIVATION_INPUT_TITLE")}
                    </h4>
                    <p className="mb-2">
                      <Trans i18nKey="LICENSE_ACTIVATION_INPUT_SUBTITLE">
                        <br></br>
                      </Trans>
                    </p>
                    <LicenseKeyForm
                      previousKeys={licenseKeys}
                      onAdd={payload =>
                        dispatch({
                          type: ActionType.AddLicense,
                          payload
                        })
                      }
                      onSubmit={payload =>
                        dispatch({
                          type: ActionType.ProcessLicenseInfo,
                          payload
                        })
                      }
                    />
                  </>
                );
              case ActivationStage.ReviewLicenseInfo:
                return (
                  <>
                    <h4 className="mb-1">
                      {t("LICENSE_ACTIVATION_REVIEW_TITLE")}
                    </h4>
                    <LicenseInfoTable tableData={tableData} />
                    <button
                      className="btn btn-primary mt-4"
                      disabled={tableData.length === 0}
                      onClick={() =>
                        dispatch({ type: ActionType.InitiateActivation })
                      }
                    >
                      {t("ACTIVATE_LICENSE")}
                    </button>
                  </>
                );
              case ActivationStage.Completed:
                return (
                  <>
                    <h4 className="mb-15">
                      {t("LICENSE_ACTIVATION_COMPLETE_TITLE")}
                    </h4>
                    <img
                      className="mb-3"
                      src={polyPlusLicense}
                      alt="Poly+ License"
                    />
                    <Link to="/manage/licensing/polyplus/setup" className="btn">
                      {t("ALL_DONE")}
                    </Link>
                  </>
                );
              default:
                return <LoadingIndicator />;
            }
          })()}
        </div>
      </div>
    </div>
  );
};
